import { MultiSelectCreatable, MultiSelectOption } from "@/components/misc/Multiselect";
import { AuthContext } from "@/context/AuthContext";
import ConductionAPI from "@/javascript/conduction-api";
import { Instrument } from "@/javascript/music-objects";
import { LiveEvent } from "@/javascript/user-objects";
import { Utils } from "@/javascript/utils";
import { Box, Button, Flex, Loader, Stack, Text } from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconAlertCircle, IconCheck } from "@tabler/icons-react";
import { useContext, useEffect, useMemo, useState } from "react";
import ConductionModal from "../ConductionModal";

interface LiveEventInstrumentAssignmentFormProps {
	liveEvent: LiveEvent;
	onClose: () => void;
	onAssigned: () => void;
}

const LiveEventInstrumentAssignmentForm: React.FC<LiveEventInstrumentAssignmentFormProps> = ({ liveEvent, onClose, onAssigned }) => {
	const [instruments, setInstruments] = useState<Instrument[] | null>(null);
	const [loadingInstruments, setLoadingInstruments] = useState<boolean>(true);

	const auth = useContext(AuthContext);
	if (!auth) {
		throw new Error("AdminLiveEventInstrumentAssignmentForm must be a child of AuthContext.");
	}
	const token = auth.token;
	if (!token) return null;

	// Set up the form with initial values and validations.
	const form = useForm({
		initialValues: {
			instruments: [] as MultiSelectOption[],
		}
	});

	const fetchInstruments = () => {
		const notificationId = Utils.displayNotification(
			"Loading Instruments",
			undefined,
			"blue",
			"loading"
		);

		ConductionAPI.getInstruments()
			.then((loadedInstruments: Instrument[] | null) => {
				if (!loadedInstruments || loadedInstruments.length === 0) {
					setInstruments(null);
					Utils.updateNotification(
						notificationId,
						"No instruments Found",
						undefined,
						"red",
						<IconAlertCircle size={16} />,
						true
					);
				} else {
					setInstruments(loadedInstruments);
					Utils.updateNotification(
						notificationId,
						"Instruments Loaded",
						"All instruments have been retrieved.",
						"green",
						<IconCheck size={16} />
					);
				}
			})
			.catch(() => {
				Utils.updateNotification(
					notificationId,
					"Error Fetching Live Events",
					"An error occurred while retrieving instruments.",
					"red",
					<IconAlertCircle size={16} />,
					true
				);
				setLoadingInstruments(false);
			});
	}

	useEffect(() => {
		fetchInstruments();
	}, [])

	useEffect(() => {
		if (instruments === null || liveEvent === null) {
			return;
		}
		const liveEventInstruments = liveEvent.getInstruments() ?? [];
		form.values.instruments = instruments
			.filter(instrument => liveEventInstruments.some(i => i.id === instrument.id))
			.map((instrument) => {
				const option: MultiSelectOption = {
					label: instrument.name,
					value: instrument.id
				};
				return option;
			});
		setLoadingInstruments(false);
	}, [instruments, liveEvent])

	const handleSubmit = async () => {
		if (!form.isValid()) {
			form.validate();
			return;
		}

		// Display a notification that deletion is in progress.
		const notificationId = Utils.displayNotification(
			"Assigning Instruments to Live Event",
			undefined,
			"blue",
			"loading"
		);

		const instrumentIds: string[] = form.values.instruments.map((s) =>
			s.value.toString()
		)

		try {
			await ConductionAPI.updateLiveEventInstruments(liveEvent.id, instrumentIds, token);

			Utils.updateNotification(
				notificationId,
				"Assigned Instruments",
				undefined,
				"green",
				<IconCheck size={16} />
			);

			onAssigned();
			onClose();
		} catch (error) {
			console.error("Error assigning to liveEvent", error);
			Utils.updateNotification(
				notificationId,
				"Error",
				"Failed to assign instrument to liveEvent.",
				"red",
				<IconAlertCircle size={16} />,
				true
			);
		}
	};

	const instrumentOptions: MultiSelectOption[] = useMemo(() => instruments?.map((i) => {
		const option: MultiSelectOption = {
			label: i.name,
			value: i.id
		};
		return option;
	}) ?? [], [instruments])

	const height = 20; // view height (vh)

	return (
		<ConductionModal
			onClose={onClose}
			title="Assign Instruments to Live Event"
			size="md"
		>
			<Box
				style={{
					minHeight: `${height}vh`
				}}
			>
				{
					(loadingInstruments) ? (
						<Loader size="xl" variant="dots" />
					) : liveEvent === null ? (
						<Text>The needed data did not load correctly.</Text>
					) : (
						<form onSubmit={form.onSubmit(handleSubmit)}>
							<Flex
								direction={"column"}
								justify={"space-between"}
								align={"stretch"}
								style={{
									minHeight: `${height}vh`
								}}
							>
								<Stack>
									<Stack>
										<Text fw={500}>Which instruments should be in the library for the {liveEvent.name} live event?</Text>
										<MultiSelectCreatable
											options={instrumentOptions}
											value={form.values.instruments}
											creatable={false}
											{...form.getInputProps("instruments")}
										/>
									</Stack>
								</Stack>
								<Button
									type="submit"
									radius="xl"
									size='sm'
									loading={form.submitting}
									color="red"
								>
									Assign Instruments to Live Event
								</Button>
							</Flex>
						</form>
					)
				}
			</Box>
		</ConductionModal>
	);
};

export default LiveEventInstrumentAssignmentForm;
